import React, { useState } from 'react';
import type { VanLifeState, VanLifeAction } from '../../../../games/van-life/van-life';
import { SUIT_COLOR, SUIT_BG, suitLabel } from './cards';
import ParkInfoPanel from './ParkInfoPanel';

interface Props {
  state: VanLifeState;
  myId: string;
  dispatch: (action: VanLifeAction) => void;
}

export default function DiscardPanel({ state, myId, dispatch }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const m = state.match!;
  const partner = state.players.find(p => p.id !== myId)!;
  const myHand = m.hands[myId] ?? [];
  const alreadyDiscarded = m.pendingDiscards[myId] ?? false;
  const partnerDiscarded = state.players
    .filter(p => p.id !== myId)
    .every(p => m.pendingDiscards[p.id]);

  const toggleDiscard = (cardId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) { next.delete(cardId); }
      else if (next.size < 2) { next.add(cardId); }
      return next;
    });
  };

  const submitDiscard = () => {
    if (selected.size !== 2) return;
    dispatch({ type: 'discardCards', playerId: myId, cardIds: Array.from(selected) });
    setSelected(new Set());
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-bold text-amber-200">
        Match {m.matchNumber} — Discard Phase
      </h2>
      {alreadyDiscarded ? (
        <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4 text-center">
          <p className="text-green-400 font-semibold">Discards submitted.</p>
          <p className="text-gray-500 text-sm mt-1">
            {partnerDiscarded ? 'Match starting…' : `Waiting for ${partner?.name}…`}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-amber-700 bg-amber-950/20 p-4">
          <p className="text-amber-300 text-sm mb-3">
            Select <strong>2 cards</strong> to discard before the match begins.
            Shape your hand around your zone / challenge target.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {myHand.map(card => {
              const isSelected = selected.has(card.id);
              const dimmed = !isSelected && selected.size >= 2;
              return (
                <button key={card.id}
                  onClick={() => toggleDiscard(card.id)}
                  className={`w-14 h-20 rounded-lg border-2 flex flex-col items-center justify-center gap-0.5
                    font-bold transition-all duration-150 cursor-pointer
                    ${isSelected ? 'ring-2 ring-red-400 ring-offset-1 ring-offset-gray-900 opacity-50 scale-95' : ''}
                    ${dimmed ? 'opacity-30' : 'opacity-100'}`}
                  style={{
                    borderColor: SUIT_COLOR[card.suit] ?? '#999',
                    background: SUIT_BG[card.suit] ?? 'rgba(0,0,0,0.1)',
                    color: SUIT_COLOR[card.suit] ?? '#999',
                  }}>
                  <span className="leading-none" style={{ fontSize: '1.1rem' }}>{suitLabel(card.suit)}</span>
                  <span className="leading-none font-black" style={{ fontSize: '1.4rem' }}>{card.rank}</span>
                  {isSelected && <span className="text-red-400 text-xs font-bold">✕</span>}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={submitDiscard}
              disabled={selected.size !== 2}
              className="px-4 py-2 bg-amber-700 hover:bg-amber-600 disabled:opacity-40
                disabled:cursor-not-allowed rounded-lg font-semibold transition-colors">
              Discard {selected.size}/2 selected
            </button>
            {selected.size > 0 && (
              <button onClick={() => setSelected(new Set())}
                className="text-sm text-gray-500 hover:text-gray-300">
                clear
              </button>
            )}
            <span className="text-gray-600 text-xs ml-auto">
              {partner?.name}: {m.pendingDiscards[partner?.id] ? 'ready' : 'choosing…'}
            </span>
          </div>
        </div>
      )}
      {/* Park info preview */}
      <div className="space-y-2">
        {state.players.map(player => {
          const park = state.parks.find(pk => pk.id === player.currentParkId)!;
          return <ParkInfoPanel key={player.id} park={park} player={player} isMe={player.id === myId} />;
        })}
      </div>
    </div>
  );
}
